import { IconsEnum } from '../../../../../assets/icons/types';
import { ClassName, I18nText } from '../../../../../types';
import { FetchUsersFilters } from '../../../../users/usersTypes';
import {
  OperationTeamNanoID,
  FetchOperationsCacheKey
} from '../../../operationsTypes';
import {
  UpdateOperationModalButtonOperation,
  UpdateOperationModalButtonAccess
} from './UpdateOperationModalButton.types';

import { useToastNotification } from '../../../../../common/hooks/useToastNotification';

import {
  UpdateOperationForm,
  useUpdateOperationForm
} from '../../forms/UpdateOperationForm';

import { AlertMessage } from '../../../../../helpers/AlertMessage';
import { FormModalButton } from '../../../../../helpers/buttons/FormModalButton';

import { operationsKeys } from '../../../../../locales/keys';

const UPDATE_OPERATION_FORM = 'update-operation-form';

interface UpdateOperationModalButtonProps {
  teamNanoId?: OperationTeamNanoID;
  operation: UpdateOperationModalButtonOperation;
  cacheKeys?: FetchOperationsCacheKey[];
  className?: ClassName;
  i18nText?: I18nText;
  tooltipI18nText?: I18nText;
  i18nTextClassName?: ClassName;
  icon?: IconsEnum;
  iconClassName?: ClassName;
  withToastOnSuccess?: boolean;
  disabled?: boolean;
  access: UpdateOperationModalButtonAccess;
  userSelectInitialFilters?: FetchUsersFilters;
}

function UpdateOperationModalButton({
  teamNanoId,
  operation,
  cacheKeys,
  className,
  i18nText,
  tooltipI18nText,
  i18nTextClassName,
  icon,
  iconClassName,
  withToastOnSuccess,
  disabled,
  access,
  userSelectInitialFilters
}: UpdateOperationModalButtonProps) {
  const { showToastI18nNotification } = useToastNotification({
    appearance: 'success',
    i18nMessage: operationsKeys.recordUpdated
  });

  const {
    validationErrors,
    registerFields,
    watchCreateCompanyRecord,
    handleUpdateOperation,
    resetUpdateOperationForm,
    updateOperationReset,
    updateOperationLoading,
    updateOperationErrorMessage,
    control,
    operationDropZoneFileAttachmentIds,
    operationDropZoneFileAttachments
  } = useUpdateOperationForm({
    teamNanoId,
    operation,
    cacheKeys,
    onSuccess: withToastOnSuccess ? showToastI18nNotification : undefined
  });

  return (
    <FormModalButton
      className={
        className ||
        'py-2 pl-2 pr-4 space-x-1 rounded-md inline-flex items-center whitespace-nowrap text-sm font-medium leading-6 focus:ring-base bg-gray-200 hover:bg-gray-300 text-gray-800 hover:text-gray-950 dark:text-gray-200 dark:hover:text-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700 focus:ring-offset-0'
      }
      form={UPDATE_OPERATION_FORM}
      i18nTitle={operationsKeys.updateRecord}
      i18nText={i18nText}
      tooltipI18nText={tooltipI18nText}
      i18nTextClassName={i18nTextClassName}
      i18nSubmitText={operationsKeys.updateRecord}
      icon={icon}
      iconClassName={iconClassName}
      onOpen={resetUpdateOperationForm}
      isLoading={updateOperationLoading}
      onClose={updateOperationReset}
      onSubmit={handleUpdateOperation}
      disabled={disabled}
    >
      <UpdateOperationForm
        form={UPDATE_OPERATION_FORM}
        teamNanoId={teamNanoId}
        watchCreateCompanyRecord={watchCreateCompanyRecord}
        isLoading={updateOperationLoading}
        control={control}
        registerBody={registerFields.registerBody}
        registerDate={registerFields.registerDate}
        registerCompleteDatetime={registerFields.registerCompleteDatetime}
        registerSubject={registerFields.registerSubject}
        registerCreateCompanyRecord={registerFields.registerCreateCompanyRecord}
        subjectValidationError={validationErrors.subjectValidationError}
        bodyValidationError={validationErrors.bodyValidationError}
        dateValidationError={validationErrors.dateValidationError}
        completeDatetimeValidationError={
          validationErrors.completeDatetimeValidationError
        }
        clientIdValidationError={validationErrors.clientIdValidationError}
        createCompanyRecordError={validationErrors.createCompanyRecordError}
        access={access}
        userSelectInitialFilters={userSelectInitialFilters}
        operationDropZoneFileAttachmentIds={operationDropZoneFileAttachmentIds}
        operationDropZoneFileAttachments={operationDropZoneFileAttachments}
      />

      <div className="px-4">
        <AlertMessage message={updateOperationErrorMessage} />
      </div>
    </FormModalButton>
  );
}

export default UpdateOperationModalButton;
